import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { hideLockExpirationWarning } from '../../../store/slices/sessionLockSlice';
import { getSessionLockService } from '../../../api/sessionLockService';
import { logger } from '../../../utils/logger';
import LHModalPop from '../../../components/LHModalPop';

/**
 * Modal component for lock expiration warning
 * Shows when lock is about to expire (30 seconds left)
 * Displays countdown timer and allows user to continue editing
 */
interface LockExpirationWarningModalProps {
  onNavigate?: () => void;
}

const LockExpirationWarningModal: React.FC<LockExpirationWarningModalProps> = ({ onNavigate }) => {
  const dispatch = useDispatch();
  const { isLockExpirationWarningVisible, lockExpirationWarningData } = useSelector(
    (state: any) => state?.sessionLock || { isLockExpirationWarningVisible: false, lockExpirationWarningData: null }
  );
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  // Log Redux state changes for debugging
  useEffect(() => {
    console.log('🔵 LockExpirationWarningModal Redux State Updated:');
    console.log('  - isLockExpirationWarningVisible:', isLockExpirationWarningVisible);
    console.log('  - lockExpirationWarningData:', lockExpirationWarningData);
    console.log('  - Will render modal:', isLockExpirationWarningVisible && !!lockExpirationWarningData);
  }, [isLockExpirationWarningVisible, lockExpirationWarningData]);

  // Update countdown timer and handle timeout expiration
  useEffect(() => {
    if (!isLockExpirationWarningVisible || !lockExpirationWarningData) {
      return;
    }

    const calculateRemainingSeconds = () => {
      const expiresAt = new Date(lockExpirationWarningData.expiresAt);
      const now = new Date();
      const diffMs = expiresAt.getTime() - now.getTime();
      const diffSeconds = Math.max(0, Math.floor(diffMs / 1000));
      return diffSeconds;
    };

    // Set initial value immediately before interval starts
    setRemainingSeconds(calculateRemainingSeconds());

    const interval = setInterval(() => {
      const diffSeconds = calculateRemainingSeconds();
      setRemainingSeconds(diffSeconds);

      // Handle timeout expiration - release lock and navigate
      if (diffSeconds <= 0) {
        clearInterval(interval);
        handleTimeoutExpiration();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isLockExpirationWarningVisible, lockExpirationWarningData, dispatch]);

  // Clear warning active flag when modal becomes hidden
  useEffect(() => {
    if (!isLockExpirationWarningVisible && lockExpirationWarningData) {
      const lockService = getSessionLockService();
      if (lockService) {
        logger.log('[LockExpirationWarningModal] Modal hidden, clearing warning active flag');
        lockService.clearWarningActiveFlag(lockExpirationWarningData.resourceId);
      }
    }
  }, [isLockExpirationWarningVisible, lockExpirationWarningData]);

  /**
   * Handle timeout expiration - release lock and close modal
   * This is called when the countdown reaches 0
   */
  const handleTimeoutExpiration = async () => {
    try {
      logger.warn('[LockExpirationWarningModal] Session timeout expired, releasing lock');
      
      if (lockExpirationWarningData) {
        const lockService = getSessionLockService();
        if (lockService) {
          // CRITICAL: Get the LATEST lock token from service, not stale Redux data
          const currentLockData = lockService.getActiveLock(lockExpirationWarningData.resourceId);
          const tokenToRelease = currentLockData?.lockToken || lockExpirationWarningData.lockToken;
          
          logger.log('[LockExpirationWarningModal] Using CURRENT token for release:', tokenToRelease?.substring(0, 20) + '...');
          
          // Release the lock with the LATEST token
          await lockService.releaseLock(
            lockExpirationWarningData.resourceId,
            tokenToRelease
          );
          logger.log('[LockExpirationWarningModal] Lock released');
          
          // Clear the warning active flag
          lockService.clearWarningActiveFlag(lockExpirationWarningData.resourceId);
          
          // Trigger the timeout expiration callback immediately for navigation
          lockService.triggerTimeoutExpirationCallback(lockExpirationWarningData.resourceId);
        }
      }
      
      // Hide the modal
      dispatch(hideLockExpirationWarning());
      
      // Call the navigation callback to handle routing (module-specific)
      if (onNavigate) {
        onNavigate();
      }
    } catch (error) {
      logger.error('[LockExpirationWarningModal] Error during timeout expiration:', error);
      dispatch(hideLockExpirationWarning());
      
      // Still call navigation callback even if lock release fails
      if (onNavigate) {
        onNavigate();
      }
    }
  };

  const handleContinue = async () => {
    try {
      if (!lockExpirationWarningData) {
        logger.warn('[LockExpirationWarningModal] No expiration warning data');
        dispatch(hideLockExpirationWarning());
        return;
      }

      const lockService = getSessionLockService();
      if (lockService) {
        // Get the configured timeout duration
        const timeoutMinutes = parseFloat(process.env.DLO_LOCK_TIMEOUT_MINUTES || '30');
        
        // CRITICAL: Get the LATEST lock token from service, not stale Redux data
        const currentLockData = lockService.getActiveLock(lockExpirationWarningData.resourceId);
        const tokenToRefresh = currentLockData?.lockToken || lockExpirationWarningData.lockToken;
        
        logger.log('[LockExpirationWarningModal] Using CURRENT token for refresh:', tokenToRefresh?.substring(0, 20) + '...');
        
        // Call heartbeat API to reset the timeout and extend lock using LATEST token
        await lockService.refreshLockExpiration(
          lockExpirationWarningData.resourceId,
          tokenToRefresh
        );

        logger.log(`[LockExpirationWarningModal] Heartbeat sent and lock refreshed for ${timeoutMinutes} more minute(s)`);
        
        // DO NOT clear the warning flag yet - it will remain active until the modal is actually hidden
        // This prevents periodic heartbeats from updating expiresAt while the new warning is being set up
      }

      // Close modal
      dispatch(hideLockExpirationWarning());
    } catch (error) {
      logger.error('[LockExpirationWarningModal] Error refreshing lock:', error);
      dispatch(hideLockExpirationWarning());
    }
  };

  const handleCancel = async () => {
    try {
      logger.log('[LockExpirationWarningModal] Go Back pressed, releasing lock and navigating to dashboard');
      
      if (lockExpirationWarningData) {
        const lockService = getSessionLockService();
        if (lockService) {
          // CRITICAL: Get the LATEST lock token from service, not stale Redux data
          const currentLockData = lockService.getActiveLock(lockExpirationWarningData.resourceId);
          const tokenToRelease = currentLockData?.lockToken || lockExpirationWarningData.lockToken;
          
          logger.log('[LockExpirationWarningModal] Using CURRENT token for release:', tokenToRelease?.substring(0, 20) + '...');
          
          // Release the lock with the LATEST token
          await lockService.releaseLock(
            lockExpirationWarningData.resourceId,
            tokenToRelease
          );
          logger.log('[LockExpirationWarningModal] Lock released successfully');
          
          // Clear the warning active flag
          lockService.clearWarningActiveFlag(lockExpirationWarningData.resourceId);
        }
      }
      
      // Hide the modal
      dispatch(hideLockExpirationWarning());
      
      // Call the navigation callback to handle routing (module-specific)
      if (onNavigate) {
        onNavigate();
      }
    } catch (error) {
      logger.error('[LockExpirationWarningModal] Error releasing lock:', error);
      dispatch(hideLockExpirationWarning());
      
      // Still call navigation callback even if lock release fails (module-specific routing)
      if (onNavigate) {
        onNavigate();
      }
    }
  };

  if (!isLockExpirationWarningVisible || !lockExpirationWarningData) {
    return null;
  }

  // Format remaining time as MM:SS
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  const timeString = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

  return (
    <LHModalPop
      visible={isLockExpirationWarningVisible}
      title="Still there?"
      description="You've been inactive for a bit, to keep your work safe you will be signed off"
      primaryButtonText="Continue editing"
      onPrimaryPress={handleContinue}
      secondaryButtonText="Go Back"
      onSecondaryPress={handleCancel}
      onClose={handleCancel}
      showFooter={true}
      showCloseButton={false}
      dismissOnBackdropPress={false}
    >
      <View style={styles.timerBox}>
        <MaterialIcons name="info-outline" size={24} color="#0066CC" style={styles.infoIcon} />
        <Text style={styles.timerText}>
          Your session will be closed in <Text style={styles.timerValue}>({timeString})</Text>
        </Text>
      </View>
    </LHModalPop>
  );
};

const styles = StyleSheet.create({
  timerBox: {
    marginVertical: 16,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#0066CC',
    backgroundColor: '#f0f7ff',
    flexDirection: 'row',
    alignItems: 'center',
  },
  infoIcon: {
    marginRight: 12,
    flexShrink: 0,
  },
  timerText: {
    fontSize: 14,
    color: '#333333',
    flex: 1,
    lineHeight: 20,
  },
  timerValue: {
    fontWeight: '600',
    color: '#0066CC',
  },
});

export default LockExpirationWarningModal;
